import { FormControl, FormControlLabel } from '@mui/material';

import MyDialog from "~/mui/MyDialog";
import { useTrans } from '~/hooks/useTrans';
import { apiEditService } from '~/providers/api';
import { myUseState } from "~/hooks/myUseState";
import { useLang } from "~/hooks/useLang";
import { IService } from "~/providers/interfaces";
import TextFieldNumber from "~/mui/TextFieldNumber";
import { MySwitch } from "~/mui/MySwitch";

interface IState extends Pick<IService, "callPrice" | "hiddenForUsers" | "regularService"> {
	dialogLoading: boolean;
	errorMsg: string;
	callPriceError: string;
}

interface IAdminEditServiceDialog {
	service: IService;
	onSave?: (data: IService) => void;
	onClose?: () => void;
}

export default function AdminEditServiceDialog({
	service,
	onSave = () => {},
	onClose = () => {},
}: IAdminEditServiceDialog) {
	const { trans } = useTrans();
	const { lang } = useLang();
	const { state, updateState } = myUseState<IState>({
		dialogLoading: false,
		errorMsg: "",
		callPrice: service.callPrice,
		hiddenForUsers: service.hiddenForUsers,
		regularService: service.regularService,
		callPriceError: "",
	});

	function isChanged() {
		return state.callPrice !== service.callPrice
			|| state.hiddenForUsers !== service.hiddenForUsers
			|| state.regularService !== service.regularService;
	}

	async function dialogOnSave() {
		if (state.callPrice < 0) {
			updateState({
				callPriceError: trans("createServiceModal.error.callPrice"),
			});

			return;
		}

		updateState({
			dialogLoading: true,
			callPriceError: "",
			errorMsg: "",
		});

		const updateData = await apiEditService(service.id, {
			callPrice: state.callPrice,
			hiddenForUsers: state.hiddenForUsers,
			regularService: state.regularService,
		}, lang);

		if (updateData.noErrors) {
			onSave(updateData.data);
		} else {
			updateState({
				dialogLoading: false,
				errorMsg: updateData.errors.join(", "),
			});
		}
	}

	return <MyDialog title={trans("adminEditServiceDialog.title")} onClose={onClose} onSave={dialogOnSave} className="adminEditServiceDialog" dialogLoading={state.dialogLoading}
		errorMsg={state.errorMsg} minWidth="300px" disableSave={!isChanged()}>
		<FormControl sx={{ width: "100%" }}>
			<TextFieldNumber
				label={trans("createServiceModal.callPrice")}
				value={state.callPrice}
				onChange={callPrice => updateState({ callPrice })}
				sx={{ width: "100%", marginBottom: "var(--margin-large)" }}
				error={state.callPriceError !== ""}
				helperText={state.callPriceError}
			/>
			<FormControlLabel
				control={<MySwitch sx={{ m: 1 }} checked={state.hiddenForUsers} onChange={event => updateState({ hiddenForUsers: event.target.checked })} />}
				label={trans("adminServicesPage.table.hiddenForUsers")}
				sx={{ marginBottom: "var(--margin-large)" }}
			/>
			<FormControlLabel
				control={<MySwitch sx={{ m: 1 }} checked={state.regularService} onChange={event => updateState({ regularService: event.target.checked })} />}
				label={trans("adminServicesPage.table.regularService")}
			/>
		</FormControl>
	</MyDialog>;
}
